import { Controller } from "@hotwired/stimulus";

// Runner de bilan single-page (sans scope). Toutes les sections sont dans le
// DOM ; on toggle leur visibilité via la TOC. Chaque champ s'auto-save vers
// /bilans/:id/answer (field_id + value/value_json/numeric_value/note/rich_value/
// attachment). Le serveur renvoie { progress, ready_to_complete } + valide les
// bornes KPI (erreur affichée inline).
export default class extends Controller {
  static targets = ["section", "tocItem", "progressTotal", "missing", "completeBtn", "fieldError"];
  static values = { answerUrl: String, currentIndex: { type: Number, default: 0 } };

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    this._showSection(this.currentIndexValue);
  }

  openSection(event) {
    this._showSection(parseInt(event.currentTarget.dataset.sectionIndex, 10));
  }

  _showSection(idx) {
    this.sectionTargets.forEach(s => { s.hidden = parseInt(s.dataset.sectionIndex, 10) !== idx; });
    this.currentIndexValue = idx;
    this.tocItemTargets.forEach(t => t.classList.toggle("aur-toc__item--active", parseInt(t.dataset.sectionIndex, 10) === idx));
  }

  answer(event) {
    const el = event.currentTarget;
    this._send({ field_id: el.dataset.fieldId, value: el.value }, el);
  }

  answerNumeric(event) {
    const el = event.currentTarget;
    this._send({ field_id: el.dataset.fieldId, numeric_value: el.value }, el);
  }

  answerMulti(event) {
    const el = event.currentTarget;
    const root = el.closest("[data-choices]");
    const values = Array.from(root.querySelectorAll('input[type="checkbox"]')).filter(c => c.checked).map(c => c.value);
    this._send({ field_id: el.dataset.fieldId, value_json: JSON.stringify(values) }, el);
  }

  answerNote(event) {
    const el = event.currentTarget;
    this._send({ field_id: el.dataset.fieldId, note: el.value }, el);
  }

  answerRich(event) {
    // trix-change bubble jusqu'au wrapper [data-field-id]. event.target est le
    // <trix-editor> ; sa valeur HTML est dans l'input caché référencé par son
    // attribut `input`.
    const editor = event.target;
    if (!editor || editor.tagName !== "TRIX-EDITOR") return;
    const wrap = editor.closest("[data-field-id]");
    const fieldId = wrap?.dataset.fieldId;
    const inputId = editor.getAttribute("input");
    const val = inputId ? document.getElementById(inputId)?.value : "";
    if (!fieldId) return;
    this._send({ field_id: fieldId, rich_value: val }, editor);
  }

  answerFile(event) {
    const el = event.currentTarget;
    const file = el.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("field_id", el.dataset.fieldId);
    fd.append("attachment", file);
    this._post(fd, el);
  }

  async _send(data, el) {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, v));
    await this._post(fd, el);
  }

  async _post(fd, el) {
    try {
      const res = await fetch(this.answerUrlValue, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this._csrf, "Accept": "application/json" },
        body: fd,
      });
      const payload = await res.json().catch(() => null);
      this._clearError(el);
      if (!res.ok) {
        if (payload?.error) this._showError(el, payload.error);
        return;
      }
      this._refresh(payload);
    } catch (_) { /* noop */ }
  }

  _refresh(payload) {
    if (!payload) return;
    if (payload.progress) {
      if (this.hasProgressTotalTarget) {
        this.progressTotalTarget.textContent = `${payload.progress.answered}/${payload.progress.total}`;
      }
      const missing = payload.progress.required_missing || 0;
      if (this.hasMissingTarget) this.missingTarget.hidden = missing === 0;
    }
    if (this.hasCompleteBtnTarget) this.completeBtnTarget.disabled = !payload.ready_to_complete;
  }

  _showError(el, msg) {
    const wrap = el.closest("[data-field-id]");
    let box = wrap?.querySelector("[data-field-error]");
    if (wrap && !box) {
      box = document.createElement("p");
      box.dataset.fieldError = "";
      box.style.color = "#dc2626";
      box.style.fontSize = "0.8rem";
      box.style.margin = "0.25rem 0 0";
      wrap.appendChild(box);
    }
    if (box) box.textContent = msg;
  }

  _clearError(el) {
    const box = el.closest("[data-field-id]")?.querySelector("[data-field-error]");
    if (box) box.textContent = "";
  }
}
